import { Box } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { countries } from 'components/CountrySelect/countries';
import { phoneExtensions } from 'components/CountrySelect/phoneExtensions';

export interface PhoneExtensionSelectProps {
  /** Currently selected phone extension e.g. "+44" */
  value: string;
  /** Called when the user picks a different extension */
  onChange: (value: string) => void;
  /** ISO country code – when it changes the dropdown auto-selects the matching extension */
  countryCode?: string;
  /** Chakra style props forwarded to the wrapper */
  [key: string]: any;
}

interface ExtensionEntry {
  code: string;
  name: string;
  dial: string;
}

const extensionEntries: ExtensionEntry[] = Object.keys(phoneExtensions)
  .map((code) => ({
    code,
    name: (countries as Record<string, string>)[code] || code,
    dial: phoneExtensions[code],
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export default function PhoneExtensionSelect({
  value,
  onChange,
  countryCode,
  ...rest
}: PhoneExtensionSelectProps) {
  const prevCountryCodeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      countryCode &&
      countryCode !== prevCountryCodeRef.current &&
      phoneExtensions[countryCode]
    ) {
      onChange(phoneExtensions[countryCode]);
    }
    prevCountryCodeRef.current = countryCode;
  }, [countryCode, onChange]);

  return (
    <Box position="relative" {...rest}>
      {/* Visual layer: shows only the dial code */}
      <Box
        pointerEvents="none"
        h="40px"
        display="flex"
        alignItems="center"
        px="1rem"
        pr="2.5rem"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        fontSize="inherit"
        color={value ? 'inherit' : 'gray.500'}
      >
        {value || '+__'}
      </Box>

      {/* Interaction layer: invisible native select on top */}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
        }}
      >
        {extensionEntries.map(({ code, name, dial }) => (
          <option key={code} value={dial}>
            ({dial}) {name}
          </option>
        ))}
      </select>
    </Box>
  );
}
